import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom";
import { Card } from "../components/Card";
import Loading from "../components/Loading";
import {
  DealInviteQuery,
  useAcceptDealInviteMutation,
  useAccountQuery,
  useCreateAccountMutation,
  useDealInviteQuery,
} from "../graphql/generated";
import useGqlClient from "../hooks/useGqlClient";
import { AuthSidebar } from "../components/auth/AuthSidebar";
import { Button } from "../components/tailwind/Button";
import { actions } from "../store/auth/slice";
import { SignIn, SignUp, useAuth, useSession } from "@clerk/clerk-react";
import { SignupSource } from "./Signup";

export function Invite() {
  return (
    <div className="flex h-screen flex-1 bg-concrete-50">
      <AuthSidebar layout="panel" />

      <div className="flex flex-1 items-center justify-center overflow-y-auto px-6 py-10">
        <div className="w-full max-w-[400px]">
          <Switch>
            <Route path="/deal-invite/:token">
              <InviteDetails />
            </Route>
          </Switch>
        </div>
      </div>
    </div>
  );
}

function InviteDetails() {
  const { token } = useParams<{ token: string }>();
  const history = useHistory();

  const [accountError, setAccountError] = useState<string | null>(null);

  const client = useGqlClient();
  const inviteQuery = useDealInviteQuery(client, {
    token,
  });

  const accountQuery = useAccountQuery(client, {});
  const account = accountQuery.data?.account;

  const emailMatch = account?.email === inviteQuery.data?.dealInvite.email;
  const emailMismatch = !emailMatch;

  useEffect(() => {
    if (accountQuery.error) {
      const errorString = JSON.stringify(accountQuery.error);
      if (errorString.includes('"message":"record not found"')) {
        setAccountError(
          "Your account isn't registered on this domain. Accounts aren't shared across regional domains.",
        );
      }
    } else {
      setAccountError(null);
    }
  }, [accountQuery.error]);

  if (accountError) {
    return (
      <InvitePanel
        title="Something went wrong"
        description={accountError}
        action={
          <Button
            text="Back to login"
            variant="positive"
            onClick={() => {
              history.push("/login");
            }}
          />
        }
      />
    );
  }

  if (account && inviteQuery.data && emailMismatch) {
    return (
      <InvitePanel
        title="Wrong account"
        description={
          <>
            This invite was sent to{" "}
            <span className="font-semibold text-persian-990">
              {inviteQuery.data.dealInvite.email}
            </span>
            , but you're signed in as{" "}
            <span className="font-semibold text-persian-990">
              {account.email}
            </span>
            . Please sign in with the invited email address.
          </>
        }
        action={
          <Button
            text="Back to login"
            variant="positive"
            onClick={() => {
              history.push("/login");
            }}
          />
        }
      />
    );
  }

  if (inviteQuery.error) {
    return (
      <InvitePanel
        title="This invite link isn't valid"
        description="It may have expired, already been used, or been restricted by your network. If you think this is a mistake, ask the person who invited you to send a new link."
        action={
          <Button
            text={account ? "Take me home" : "Back to login"}
            variant="positive"
            onClick={() => {
              history.push(account ? "/deals" : "/login");
            }}
          />
        }
      />
    );
  }

  if (inviteQuery.isPending || !inviteQuery.data) {
    return <Loading />;
  }

  const invite = inviteQuery.data.dealInvite;
  const invitedBy = invite.invitedBy;

  return (
    <div className="flex flex-col">
      <div className="text-center">
        <p className="mt-2 text-sm leading-relaxed text-concrete-600 text-pretty">
          <span className="font-medium text-persian-990">{invitedBy.name}</span>
          {invitedBy.firm ? (
            <>
              {" "}
              from{" "}
              <span className="font-medium text-persian-990">
                {invitedBy.firm.name}
              </span>
            </>
          ) : null}{" "}
          invited you to collaborate.
        </p>
      </div>

      <div className="mt-6">
        <CardContent dealInvite={invite} />
      </div>
    </div>
  );
}

function InvitePanel(props: {
  title: string;
  description: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="text-center">
      <h1 className="text-2xl font-semibold tracking-tight text-persian-990">
        {props.title}
      </h1>
      <p className="mt-2 text-[15px] leading-relaxed text-concrete-600 text-pretty">
        {props.description}
      </p>
      {props.action ? (
        <div className="mt-6 flex justify-center">{props.action}</div>
      ) : null}
    </div>
  );
}

function CardContent(props: { dealInvite: DealInviteQuery["dealInvite"] }) {
  const { token } = useParams<{ token: string }>();
  const client = useGqlClient();
  const accountQuery = useAccountQuery(client, {});
  const account = accountQuery.data?.account;
  const history = useHistory();

  const { session, isSignedIn } = useSession();
  const createAccount = useCreateAccountMutation(client);
  const acceptDealInvite = useAcceptDealInviteMutation(client);
  const dispatch = useDispatch();

  const [accountError, setAccountError] = useState<string | null>(null);

  useEffect(() => {
    if (accountQuery.error) {
      const errorString = JSON.stringify(accountQuery.error);
      if (errorString.includes('"message":"record not found"')) {
        setAccountError(
          "Your account isn't registered on this domain. Accounts aren't shared across regional domains.",
        );
      }
    } else {
      setAccountError(null);
    }
  }, [accountQuery.error]);

  useEffect(() => {
    if (!isSignedIn) {
      return;
    }

    if (!session || !session.user) {
      return;
    }

    if (!session.user.unsafeMetadata) {
      return;
    }

    if (session.user.unsafeMetadata.onboardingStatus !== "initial") {
      return;
    }

    if (createAccount.isPending || acceptDealInvite.isPending) {
      return;
    }

    if (account) {
      return;
    }

    createAccount.mutate(
      {
        clerkUserId: session.user.id,
      },
      {
        onSuccess: (data) => {
          dispatch(actions.refreshAccount({ account: data.createAccount }));

          acceptDealInvite.mutate(
            {
              token,
              ndaSigned: true,
            },
            {
              onSuccess: (acceptData) => {
                accountQuery.refetch().then((accountData) => {
                  if (accountData.data) {
                    dispatch(
                      actions.refreshAccount({
                        account: accountData.data.account,
                      }),
                    );
                  }

                  history.push(
                    `/deal/${acceptData.acceptDealInvite.id}/dashboard`,
                  );
                });
              },
            },
          );
        },
      },
    );
  }, [
    isSignedIn,
    session,
    session?.user.unsafeMetadata,
    (client as any).options,
  ]);

  if (accountError) {
    return (
      <Card padding="m">
        <p className="font-semibold text-persian-990">Something went wrong</p>
        <p className="mt-1 text-sm text-concrete-600">{accountError}</p>
        <div className="mt-6 flex justify-end">
          <Button
            onClick={() => {
              window.location.href = "/login";
            }}
            text="Back to login"
            variant="positive"
          />
        </div>
      </Card>
    );
  }

  if (!isSignedIn) {
    return <Authenticate dealInvite={props.dealInvite} />;
  }

  if (
    !session ||
    !session.user ||
    !account ||
    createAccount.isPending ||
    acceptDealInvite.isPending
  ) {
    return <Loading />;
  }

  return <AcceptInvite />;
}

function AcceptInvite() {
  const client = useGqlClient();
  const accountQuery = useAccountQuery(client, {});
  const { signOut } = useAuth();

  const dispatch = useDispatch();
  const history = useHistory();

  const acceptDealInvite = useAcceptDealInviteMutation(client);

  const { token } = useParams<{ token: string }>();

  function acceptInvitation() {
    acceptDealInvite.mutate(
      {
        token,
        ndaSigned: true,
      },
      {
        onSuccess: (data) => {
          accountQuery.refetch().then((accountData) => {
            if (accountData.data) {
              dispatch(
                actions.refreshAccount({ account: accountData.data.account }),
              );

              history.push(`/deal/${data.acceptDealInvite.id}/dashboard`);
            }
          });
        },
      },
    );
  }

  return (
    <div className="flex items-center justify-center gap-3">
      <Button
        variant="secondary"
        text="Log out"
        isDisabled={acceptDealInvite.isPending}
        onClick={async () => {
          await signOut();
          dispatch(actions.logout());
        }}
      />
      <Button
        variant="positive"
        text="Accept invitation"
        loadingText="Accepting..."
        isLoading={acceptDealInvite.isPending}
        onClick={() => {
          acceptInvitation();
        }}
      />
    </div>
  );
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function Authenticate(props: { dealInvite: DealInviteQuery["dealInvite"] }) {
  const queryParams = useQuery();
  const location = useLocation();

  const [mode, setMode] = useState<"login" | "signup">("signup");

  useEffect(() => {
    if (queryParams.get("mode") === "login") {
      setMode("login");
    }
  }, [queryParams.toString()]);

  const clerkAppearance = {
    layout: {
      socialButtonsPlacement: "bottom" as const,
      socialButtonsVariant: "iconButton" as const,
    },
    elements: {
      logoBox: "hidden",
    },
  };

  return mode === "login" ? (
    <SignIn
      appearance={clerkAppearance}
      initialValues={{ emailAddress: props.dealInvite.email }}
      signUpUrl={`${location.pathname}?mode=signup`}
      redirectUrl={undefined}
      afterSignInUrl={location.pathname}
    />
  ) : (
    <SignUp
      appearance={clerkAppearance}
      initialValues={{ emailAddress: props.dealInvite.email }}
      signInUrl={`${location.pathname}?mode=login`}
      fallbackRedirectUrl={location.pathname}
      unsafeMetadata={{
        onboardingStatus: "initial",
        signupSource: SignupSource.externalInvite,
      }}
    />
  );
}
